import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FiSave, 
  FiX, 
  FiUser, 
  FiPhone, 
  FiGlobe, 
  FiCheckCircle, 
  FiMapPin, 
  FiMap, 
  FiShuffle,
  FiCheck,
  FiPlus,
  FiTrash2,
  FiSmartphone,
  FiMonitor,
  FiLoader,
  FiArrowLeft
} from 'react-icons/fi';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import BaseUrl from '../../../Url/BaseUrl';

const RegisterOperator = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [masterData, setMasterData] = useState({
    states: [],
    zones: []
  });
  
  const [formData, setFormData] = useState({
    operatorNumber: '',
    operatorNameEn: '',
    operatorNameHi: '',
    status: true,
    mobileNumbers: [{ dialCode: '91', number: '' }],
    googlePlayStoreLink: '',
    appleAppStoreLink: '',
    bookingWebsiteLink: '',
    stateCode: '',
    state: null,
    zoneCode: '',
    zone: null
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loadingMasterData, setLoadingMasterData] = useState({
    states: false,
    zones: false
  });
  const [apiError, setApiError] = useState('');
  const [formVisible, setFormVisible] = useState(false);
  const [operatorNumberStatus, setOperatorNumberStatus] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [filteredZones, setFilteredZones] = useState([]);

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const fetchStates = async () => {
    setLoadingMasterData(prev => ({ ...prev, states: true }));
    try {
      const response = await axios.get(`${BaseUrl.baseurl}States`, {
        headers: { 'accept': 'text/plain' }
      });
      setMasterData(prev => ({ ...prev, states: response.data }));
    } catch (error) {
      console.error('Error fetching states:', error);
      throw error;
    } finally {
      setLoadingMasterData(prev => ({ ...prev, states: false }));
    }
  };

  const fetchZones = async () => {
    setLoadingMasterData(prev => ({ ...prev, zones: true }));
    try {
      const response = await axios.get(`${BaseUrl.baseurl}Zones`, {
        headers: { 'accept': 'text/plain' }
      });
      setMasterData(prev => ({ ...prev, zones: response.data }));
    } catch (error) {
      console.error('Error fetching zones:', error);
      throw error;
    } finally {
      setLoadingMasterData(prev => ({ ...prev, zones: false }));
    }
  };

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        await fetchStates();
        await fetchZones();
        setFormVisible(true);
      } catch (error) {
        setApiError(t('failedToLoadData', 'Failed to load required data.'));
        setFormVisible(true);
      }
    };
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (formData.stateCode && masterData.zones.length > 0) {
      const zonesForState = masterData.zones.filter(zone => zone.stateCode === formData.stateCode);
      setFilteredZones(zonesForState);
      if (!zonesForState.some(z => z.zoneCode === formData.zoneCode)) {
        setFormData(prev => ({ ...prev, zoneCode: '', zone: null }));
      }
    } else {
      setFilteredZones([]);
      setFormData(prev => ({ ...prev, zoneCode: '', zone: null }));
    }
  }, [formData.stateCode, masterData.zones]);

  // Auto-check when operator number changes (debounced)
  useEffect(() => {
    if (formData.operatorNumber && /^4\d{7}$/.test(formData.operatorNumber)) {
      const debouncedCheck = debounce(async () => {
        try {
          setCheckingAvailability(true);
          const response = await axios.get(
            `${BaseUrl.baseurl}BusOperators/CheckOperatorNo`,
            { params: { id: formData.operatorNumber }, headers: { 'accept': 'text/plain' } }
          );
          const isTaken = response.data;
          setOperatorNumberStatus(isTaken ? 'taken' : 'available');
        } catch (error) {
          console.error('Auto-check error:', error);
          setOperatorNumberStatus(null);
        } finally {
          setCheckingAvailability(false);
        }
      }, 500);
      debouncedCheck();
    } else if (formData.operatorNumber && !/^4\d{7}$/.test(formData.operatorNumber)) {
      setOperatorNumberStatus('invalid');
    } else {
      setOperatorNumberStatus(null);
    }
  }, [formData.operatorNumber]);

  // Generate random operator number
  const generateRandomOperatorNumber = () => {
    const min = 40000000;
    const max = 49999999;
    const randomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
    const numberToSet = randomNumber.toString();
    setFormData(prev => ({ ...prev, operatorNumber: numberToSet }));
    setOperatorNumberStatus(null);
    if (validationErrors.operatorNumber) {
      setValidationErrors(prev => ({ ...prev, operatorNumber: '' }));
    }
    toast.info(t('numberGenerated', 'Operator number generated. Click Check to verify availability.'));
  };

  // Manual check availability
  const checkOperatorNumberAvailability = async () => {
    if (!formData.operatorNumber) {
      toast.error(t('enterOperatorNumber', 'Please enter an operator number to check'));
      return;
    }
    if (!/^4\d{7}$/.test(formData.operatorNumber)) {
      setOperatorNumberStatus('invalid');
      toast.error(t('operatorNumberFormat', 'Operator number must be 8 digits starting with 4'));
      return;
    }
    setCheckingAvailability(true);
    try {
      const response = await axios.get(
        `${BaseUrl.baseurl}BusOperators/CheckOperatorNo`,
        { params: { id: formData.operatorNumber }, headers: { 'accept': 'text/plain' } }
      );
      const isTaken = response.data;
      if (isTaken) {
        setOperatorNumberStatus('taken');
        toast.error(t('operatorNumberTaken', 'This operator number is already taken'));
      } else {
        setOperatorNumberStatus('available');
        toast.success(t('operatorNumberAvailable', 'Operator number is available!'), { 
          style: {
            background: '#ffffff',
            color: '#1f2937',
            border: '1px solid #e5e7eb'
          }
        });
      }
    } catch (error) {
      console.error('Error checking operator number:', error);
      toast.error(t('checkFailed', 'Failed to check availability'));
      setOperatorNumberStatus(null);
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Mobile number handlers
  const handleMobileNumberChange = (index, field, value) => {
    const updatedMobileNumbers = [...formData.mobileNumbers];
    updatedMobileNumbers[index][field] = value;
    setFormData(prev => ({ ...prev, mobileNumbers: updatedMobileNumbers }));
    // Clear validation error for this mobile number
    if (validationErrors[`mobileNumber${index}`]) {
      setValidationErrors(prev => ({ ...prev, [`mobileNumber${index}`]: '' }));
    }
  };

  const addMobileNumberField = () => {
    if (formData.mobileNumbers.length < 5) {
      setFormData(prev => ({
        ...prev,
        mobileNumbers: [...prev.mobileNumbers, { dialCode: '91', number: '' }]
      }));
    } else {
      toast.warning(t('maxMobileNumbersReached', 'Maximum 5 mobile numbers allowed'));
    }
  };

  const removeMobileNumberField = (index) => {
    if (formData.mobileNumbers.length > 1) {
      setFormData(prev => ({
        ...prev,
        mobileNumbers: prev.mobileNumbers.filter((_, i) => i !== index)
      }));
      // Clear validation error for this mobile number
      if (validationErrors[`mobileNumber${index}`]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`mobileNumber${index}`];
          return newErrors;
        });
      }
    }
  };

  const validateMobileNumber = (index) => {
    const mobile = formData.mobileNumbers[index];
    if (!mobile.number) {
      toast.error(t('enterMobileNumber', 'Please enter a mobile number'));
      return;
    }
    if (!/^\d{10,15}$/.test(mobile.number)) {
      setValidationErrors(prev => ({
        ...prev,
        [`mobileNumber${index}`]: t('invalidMobileNumberFormat')
      }));
      toast.error(t('invalidMobileNumberFormat'));
    } else {
      toast.success(t('mobileNumberValid', 'Mobile number is valid!'));
      setValidationErrors(prev => ({
        ...prev,
        [`mobileNumber${index}`]: ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear validation error for this field if it exists
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = async () => {
    const errors = {};
    // operator number validation (includes final availability check)
    if (!formData.operatorNumber.trim()) {
      errors.operatorNumber = t('operatorNumberRequired');
      setValidationErrors(errors);
      return false;
    }
    if (!/^4\d{7}$/.test(formData.operatorNumber.trim())) {
      errors.operatorNumber = t('operatorNumberFormatValidation');
      setValidationErrors(errors);
      return false;
    }
    // Final availability check before submit
    try {
      const response = await axios.get(
        `${BaseUrl.baseurl}BusOperators/CheckOperatorNo`,
        { params: { id: formData.operatorNumber }, headers: { 'accept': 'text/plain' } }
      );
      if (response.data) {
        errors.operatorNumber = t('operatorNumberAlreadyTaken');
        setValidationErrors(errors);
        toast.error(t('operatorNumberAlreadyTaken'));
        return false;
      }
    } catch (error) {
      toast.error(t('availabilityCheckFailed'));
      return false;
    }

    if (!formData.operatorNameEn.trim()) errors.operatorNameEn = t('englishNameRequired');
    if (!formData.stateCode) errors.stateCode = t('stateRequired');
    if (!formData.zoneCode) errors.zoneCode = t('zoneRequired');

    formData.mobileNumbers.forEach((mobile, index) => {
      if (mobile.number && !/^\d{10,15}$/.test(mobile.number)) {
        errors[`mobileNumber${index}`] = t('invalidMobileNumberFormat');
      }
    });
    // URL validations
    if (formData.bookingWebsiteLink && !/^https?:\/\/.+/.test(formData.bookingWebsiteLink))
      errors.bookingWebsiteLink = t('invalidUrlFormat');
    if (formData.googlePlayStoreLink && !/^https?:\/\/.+/.test(formData.googlePlayStoreLink))
      errors.googlePlayStoreLink = t('invalidUrlFormat');
    if (formData.appleAppStoreLink && !/^https?:\/\/.+/.test(formData.appleAppStoreLink))
      errors.appleAppStoreLink = t('invalidUrlFormat');

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await validateForm())) return;
    setIsSubmitting(true);
    setApiError('');
    try {
      const validMobileNumbers = formData.mobileNumbers
        .filter(mobile => mobile.number && mobile.number.trim() !== '')
        .map(mobile => `+${mobile.dialCode}${mobile.number}`);
      const submissionData = {
        operatorNumber: formData.operatorNumber,
        operatorName: formData.operatorNameEn,
        hindi: formData.operatorNameHi,
        status: formData.status,
        busOperatorContactNumber: validMobileNumbers,
        appLink: formData.googlePlayStoreLink || null,
        iOSAppLink: formData.appleAppStoreLink || null,
        websiteContactLink: formData.bookingWebsiteLink || null,
        stateCode: formData.stateCode,
        zoneCode: formData.zoneCode
      };
      await axios.post(`${BaseUrl.baseurl}BusOperators`, submissionData, {
        headers: { 'accept': 'text/plain', 'Content-Type': 'application/json' }
      });
      toast.success(t('operatorRegistered'));
      setSuccessMessage(t('operatorRegistered'));
      setTimeout(() => navigate('/bus-finder/operators/manage'), 2000);
    } catch (error) {
      console.error(error);
      setApiError(`${t('registrationFailed')}: ${error.response?.data?.message || error.message}`);
      toast.error(error.response?.data?.message || t('registrationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/busfinderpanel');
  };

  const handleRetryFetch = async () => {
    setApiError('');
    try {
      await fetchStates();
      await fetchZones();
      toast.success(t('dataLoaded', 'Data loaded successfully'));
    } catch (error) {
      setApiError(t('failedToLoadData', 'Failed to load required data.'));
    }
  };

  // Render
  return (
    <div className="form-container">
      <ToastContainer />
      <div className="form-header">
        <div className="header-left">
          <button type="button" className="btn-back" onClick={handleCancel} disabled={isSubmitting}>
            <FiArrowLeft /> {t('back')}
          </button>
          <div className="header-title">
            <h1><FiUser /> {t('registerNewBusOperator')}</h1>
            <p>{t('fillDetailsBelow')}</p>
          </div>
        </div>
      </div>

      {apiError && <div className="alert alert-error"><span>{apiError}</span><button type="button" className="btn-retry" onClick={handleRetryFetch}>{t('retry')}</button></div>}
      {successMessage && <div className="alert alert-success"><FiCheckCircle /> {successMessage}</div>}

      {!formVisible ? (
        <div className="form-loading"><div className="spinner-large"></div><p>{t('loadingFormData')}</p></div>
      ) : (
        <form onSubmit={handleSubmit} className="form">
          {/* Operator Identification */}
          <div className="form-section">
            <h3>{t('operatorIdentification')}</h3>
            <div className="form-group">
              <label>{t('operatorNumberLabel')} *</label>
              <div className="input-with-buttons">
                <input 
                  type="text" 
                  name="operatorNumber" 
                  value={formData.operatorNumber} 
                  onChange={handleChange}
                  placeholder={t('operatorNumberPlaceholder')} 
                  disabled={isSubmitting} 
                  maxLength="8"
                  className={validationErrors.operatorNumber ? 'error' : ''} 
                />
                <div className="input-buttons">
                  <button type="button" className="btn-generate" onClick={generateRandomOperatorNumber} disabled={isSubmitting}>
                    <FiShuffle /> {t('generate')}
                  </button>
                  <button type="button" className="btn-check" onClick={checkOperatorNumberAvailability}
                    disabled={!formData.operatorNumber || isSubmitting || checkingAvailability}>
                    {checkingAvailability ? <FiLoader className="spin" /> : <FiCheck />} {t('check')}
                  </button>
                </div>
              </div>
              {operatorNumberStatus === 'taken' && <div className="status-indicator error">✗ {t('operatorNumberTakenIndicator')}</div>}
              {operatorNumberStatus === 'invalid' && <div className="status-indicator warning">! {t('operatorNumberInvalidFormat')}</div>}
              {validationErrors.operatorNumber && <span className="error-text">{validationErrors.operatorNumber}</span>}
              <div className="hint">{t('operatorNumberHint')}</div>
            </div>
          </div>

          {/* Operator Name */}
          <div className="form-section">
            <h3>{t('operatorNameTitle')}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>{t('englishName')} *</label>
                <input 
                  type="text" 
                  name="operatorNameEn" 
                  value={formData.operatorNameEn} 
                  onChange={handleChange}
                  placeholder={t('englishNamePlaceholder')} 
                  disabled={isSubmitting}
                  className={validationErrors.operatorNameEn ? 'error' : ''} 
                />
                {validationErrors.operatorNameEn && <span className="error-text">{validationErrors.operatorNameEn}</span>}
                <div className="hint">{t('englishNameHint')}</div>
              </div>
              <div className="form-group">
                <label>{t('hindiName')}</label>
                <input 
                  type="text" 
                  name="operatorNameHi" 
                  value={formData.operatorNameHi} 
                  onChange={handleChange}
                  placeholder={t('hindiNamePlaceholder')} 
                  disabled={isSubmitting} 
                />
                <div className="hint">{t('hindiNameHint')}</div>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="form-section">
            <h3>{t('locationInformation')}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label><FiMapPin /> {t('state')} *</label>
                <select 
                  name="stateCode" 
                  value={formData.stateCode} 
                  onChange={handleChange} 
                  disabled={loadingMasterData.states || isSubmitting}
                  className={validationErrors.stateCode ? 'error' : ''}
                >
                  <option value="">{t('selectState')}</option>
                  {masterData.states.map(state => <option key={state.stateCode} value={state.stateCode}>{state.stateName}</option>)}
                </select>
                {validationErrors.stateCode && <span className="error-text">{validationErrors.stateCode}</span>}
              </div>
              <div className="form-group">
                <label><FiMap /> {t('zone')} *</label>
                <select 
                  name="zoneCode" 
                  value={formData.zoneCode} 
                  onChange={handleChange}
                  disabled={loadingMasterData.zones || isSubmitting || !formData.stateCode}
                  className={validationErrors.zoneCode ? 'error' : ''}
                >
                  <option value="">{t('selectZone')}</option>
                  {filteredZones.map(zone => <option key={zone.zoneCode} value={zone.zoneCode}>{zone.zoneName}</option>)}
                </select>
                {validationErrors.zoneCode && <span className="error-text">{validationErrors.zoneCode}</span>}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-section">
            <h3>{t('contactInformation')}</h3>
            <div className="mobile-numbers-section">
              {formData.mobileNumbers.map((mobile, index) => (
                <div key={index} className="mobile-number-group">
                  <div className="form-group">
                    <label><FiPhone /> {t('mobileNumber')} {index > 0 ? `#${index+1}` : ''}</label>
                    <div className="phone-input-wrapper">
                      <select 
                        value={mobile.dialCode} 
                        onChange={(e) => handleMobileNumberChange(index, 'dialCode', e.target.value)}
                        className="country-code-select" 
                        disabled={isSubmitting}
                      >
                        <option value="91">🇮🇳 +91 (India)</option>
                        <option value="1">🇺🇸 +1 (USA)</option>
                        <option value="44">🇬🇧 +44 (UK)</option>
                        <option value="61">🇦🇺 +61 (Australia)</option>
                        <option value="81">🇯🇵 +81 (Japan)</option>
                        <option value="86">🇨🇳 +86 (China)</option>
                        <option value="49">🇩🇪 +49 (Germany)</option>
                        <option value="33">🇫🇷 +33 (France)</option>
                        <option value="39">🇮🇹 +39 (Italy)</option>
                        <option value="55">🇧🇷 +55 (Brazil)</option>
                        <option value="7">🇷🇺 +7 (Russia)</option>
                        <option value="82">🇰🇷 +82 (South Korea)</option>
                        <option value="34">🇪🇸 +34 (Spain)</option>
                        <option value="31">🇳🇱 +31 (Netherlands)</option>
                        <option value="46">🇸🇪 +46 (Sweden)</option>
                      </select>
                      <input 
                        type="tel" 
                        value={mobile.number} 
                        onChange={(e) => handleMobileNumberChange(index, 'number', e.target.value)}
                        placeholder={t('mobileNumberPlaceholder')} 
                        disabled={isSubmitting} 
                        maxLength="15" 
                      />
                      <button type="button" className="btn-validate-number" onClick={() => validateMobileNumber(index)}
                        disabled={isSubmitting || !mobile.number}>
                        <FiCheck /> {t('validate')}
                      </button>
                      {index > 0 && <button type="button" className="btn-remove-number" onClick={() => removeMobileNumberField(index)}
                        disabled={isSubmitting}><FiTrash2 /></button>}
                    </div>
                    {validationErrors[`mobileNumber${index}`] && <span className="error-text">{validationErrors[`mobileNumber${index}`]}</span>}
                    <div className="hint">{t('mobileNumberHint')}</div>
                  </div>
                </div>
              ))}
              <button type="button" className="btn-add-number" onClick={addMobileNumberField} disabled={isSubmitting}>
                <FiPlus /> {t('addAnotherMobileNumber')}
              </button>
            </div>
          </div>

          {/* Digital Presence */}
          <div className="form-section">
            <h3>{t('digitalPresence')}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label><FiGlobe /> {t('bookingWebsite')}</label>
                <input 
                  type="url" 
                  name="bookingWebsiteLink" 
                  value={formData.bookingWebsiteLink} 
                  onChange={handleChange}
                  placeholder={t('bookingWebsitePlaceholder')} 
                  disabled={isSubmitting}
                  className={validationErrors.bookingWebsiteLink ? 'error' : ''} 
                />
                {validationErrors.bookingWebsiteLink && <span className="error-text">{validationErrors.bookingWebsiteLink}</span>}
              </div>
              <div className="form-group">
                <label><FiSmartphone /> {t('googlePlayStoreLink')}</label>
                <input 
                  type="url" 
                  name="googlePlayStoreLink" 
                  value={formData.googlePlayStoreLink} 
                  onChange={handleChange}
                  placeholder={t('googlePlayStorePlaceholder')} 
                  disabled={isSubmitting}
                  className={validationErrors.googlePlayStoreLink ? 'error' : ''} 
                />
                {validationErrors.googlePlayStoreLink && <span className="error-text">{validationErrors.googlePlayStoreLink}</span>}
              </div>
              <div className="form-group">
                <label><FiMonitor /> {t('appleAppStoreLink')}</label>
                <input 
                  type="url" 
                  name="appleAppStoreLink" 
                  value={formData.appleAppStoreLink} 
                  onChange={handleChange}
                  placeholder={t('appleAppStorePlaceholder')} 
                  disabled={isSubmitting}
                  className={validationErrors.appleAppStoreLink ? 'error' : ''} 
                />
                {validationErrors.appleAppStoreLink && <span className="error-text">{validationErrors.appleAppStoreLink}</span>}
              </div>
            </div>
          </div>

          {/* Status Settings */}
          <div className="form-section">
            <h3>{t('statusSettings')}</h3>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  name="status" 
                  checked={formData.status} 
                  onChange={handleChange} 
                  disabled={isSubmitting} 
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t('activeStatus')}</span>
              </label>
              <p className="hint">{t('activeStatusHint')}</p>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleCancel} disabled={isSubmitting}>
              <FiX /> {t('cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? <><div className="spinner-small"></div> {t('registering')}</> : <><FiSave /> {t('registerOperator')}</>}
            </button>
          </div>
        </form>
      )}

      <style jsx>{`
        .form-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 2rem;
        }

        .form-header {
          margin-bottom: 2rem;
        }

        .header-left {
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
        }

        .btn-back {
          padding: 0.5rem 1rem;
          background: #f8f9fa;
          border: 2px solid #dee2e6;
          border-radius: 8px;
          color: #333;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
          transition: all 0.2s;
          text-decoration: none;
        }

        .btn-back:hover:not(:disabled) {
          background: #e9ecef;
          border-color: #adb5bd;
          transform: translateX(-2px);
        }

        .btn-back:active:not(:disabled) {
          transform: translateX(0);
        }

        .btn-back:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .header-title {
          flex: 1;
        }

        .form-header h1 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: #333;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-header p {
          color: #666;
          font-size: 1rem;
        }

        .form-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem;
          text-align: center;
        }

        .form-loading p {
          margin-top: 1rem;
          color: #666;
        }

        .alert {
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .alert-error {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .alert-success {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .btn-retry {
          background-color: #dc3545;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
        }

        .btn-retry:hover:not(:disabled) {
          background-color: #c82333;
        }

        .btn-retry:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .input-with-buttons {
          display: flex;
          gap: 8px;
        }

        .input-with-buttons input {
          flex: 1;
        }

        .input-buttons {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 100px;
        }

        .input-with-button {
          display: flex;
          gap: 8px;
        }

        .input-with-button input {
          flex: 1;
        }

        .phone-input-wrapper {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .country-code-select {
          width: 140px;
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.2s;
          background: #f8f9fa;
          cursor: pointer;
        }

        .country-code-select:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .btn-generate,
        .btn-check,
        .btn-validate,
        .btn-validate-number,
        .btn-remove-number {
          padding: 8px 12px;
          font-size: 14px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .btn-generate {
          background-color: #f0f0f0;
          color: #333;
        }

        .btn-generate:hover:not(:disabled) {
          background-color: #e0e0e0;
        }

        .btn-check,
        .btn-validate,
        .btn-validate-number {
          background-color: #007bff;
          color: white;
        }

        .btn-check:hover:not(:disabled),
        .btn-validate:hover:not(:disabled),
        .btn-validate-number:hover:not(:disabled) {
          background-color: #0056b3;
        }

        .btn-remove-number {
          background-color: #dc3545;
          color: white;
          padding: 8px;
        }

        .btn-remove-number:hover:not(:disabled) {
          background-color: #c82333;
        }

        .btn-add-number {
          padding: 10px 16px;
          background-color: #28a745;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          margin-top: 10px;
        }

        .btn-add-number:hover:not(:disabled) {
          background-color: #218838;
        }

        .status-indicator {
          margin-top: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .status-indicator.success {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .status-indicator.error {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .status-indicator.warning {
          background-color: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        }

        .status-indicator.info {
          background-color: #d1ecf1;
          color: #0c5460;
          border: 1px solid #bee5eb;
        }

        .success {
          border-color: #28a745 !important;
          box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
        }

        .mobile-numbers-section {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .mobile-number-group {
          border-bottom: 1px solid #eee;
          padding-bottom: 1.5rem;
        }

        .mobile-number-group:last-child {
          border-bottom: none;
        }

        .spinner-tiny {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(102, 126, 234, 0.3);
          border-radius: 50%;
          border-top-color: #667eea;
          animation: spin 0.8s linear infinite;
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 0.8s linear infinite;
        }

        .spinner-large {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(102, 126, 234, 0.3);
          border-radius: 50%;
          border-top-color: #667eea;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-indicator {
          display: inline-flex;
          margin-left: 8px;
        }

        .form-section {
          margin-bottom: 2.5rem;
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .form-section h3 {
          margin: 0 0 1.5rem 0;
          color: #333;
          font-size: 1.2rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #f0f0f0;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-weight: 500;
          color: #495057;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-group input,
        .form-group select {
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.2s;
          background: #f8f9fa;
        }

        .form-group select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23333' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          background-size: 16px;
          padding-right: 2.5rem;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .form-group input.error,
        .form-group select.error {
          border-color: #dc3545;
          background: #fff5f5;
        }

        .error-text {
          color: #dc3545;
          font-size: 0.85rem;
          margin-top: 0.25rem;
        }

        .hint {
          color: #6c757d;
          font-size: 0.85rem;
          margin-top: 0.25rem;
          font-style: italic;
        }

        .checkbox-group {
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
          user-select: none;
        }

        .checkbox-label input[type="checkbox"] {
          display: none;
        }

        .checkbox-custom {
          width: 20px;
          height: 20px;
          border: 2px solid #667eea;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .checkbox-label input[type="checkbox"]:checked + .checkbox-custom::after {
          content: '✓';
          color: #667eea;
          font-size: 14px;
          font-weight: bold;
        }

        .checkbox-text {
          font-weight: 500;
          color: #495057;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          padding-top: 2rem;
          margin-top: 2rem;
          border-top: 1px solid #dee2e6;
        }

        .btn-secondary, .btn-primary {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          border: none;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #5a6268;
        }

        .btn-primary {
          background: linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%);
          color: black;
        }

        .btn-primary:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .btn-secondary:disabled,
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .form-container {
            padding: 1rem;
          }

          .header-left {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .btn-back {
            justify-content: center;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .input-with-buttons,
          .input-with-button {
            flex-direction: column;
          }

          .input-buttons {
            flex-direction: row;
            justify-content: flex-start;
          }

          .phone-input-wrapper {
            flex-direction: column;
          }

          .country-code-select {
            width: 100%;
          }

          .form-actions {
            flex-direction: column;
          }

          .btn-secondary,
          .btn-primary {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default RegisterOperator;